import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  SquaresFour,
  ChartLineUp,
  Warehouse,
  Package,
  Stack,
  Ruler,
  ClipboardText,
  Truck,
  HandGrabbing,
  Basket,
  ArrowsLeftRight,
  Scales,
  ArrowsClockwise,
  Tray,
  ListChecks,
  Users,
  UsersThree,
  ChartBar,
  Monitor,
  Bell,
  GearSix,
  CaretLeft,
  CaretRight,
  CaretDown,
  CaretUp,
  FileText,
  PuzzlePiece,
  Checks,
  Warning,
  Scissors,
  ListNumbers,
  Barcode,
  Scan,
  Tag,
  TagSimple,
  CookingPot,
  MapPin,
  ClockCounterClockwise,
  Factory,
  ShieldCheck,
  Shield,
  Columns,
  Broom,
  UploadSimple,
  Key,
  ChatCircle,
  Database,
  Wrench,
  CalendarBlank,
  Receipt,
  DotsThreeOutline,
  Sparkle,
  Images,
  BookOpen,
  SealCheck,
  CloudArrowUp,
  Palette,
  Code,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { RULE_PAGES } from '@/lib/rule-pages';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  useFilteredNavigation,
  isNavGroup,
  navItemPaths,
  type NavGroup,
  type NavItem,
  type NavSection,
} from '@/hooks/useFilteredNavigation';
import { useAiAssistantAccess } from '@/api/hooks/useAiAssistant';
import { useFillFlow } from '@/api/hooks/useSettings';
import { useActiveWarehouse } from '@/hooks/useActiveWarehouse';
import {
  useEndCountEnabled,
  useKitchenEndCountOn,
  useSawLabelsEnabled,
} from '@/api/hooks/useManufacturingFeatures';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
  /**
   * Render submenus as inline accordions instead of hover flyouts — for the
   * mobile drawer, where there is no hover.
   */
  inlineSubmenus?: boolean;
}

/** Submenus longer than this render in two columns so they fit on screen. */
const TWO_COLUMN_THRESHOLD = 10;

const testId = (name: string) => name.toLowerCase().replace(/\s+/g, '-');

/**
 * Is one of a group's links the current page? Matched on each href's PATH: an
 * entry may carry a query (Mfg Work Orders opens `?view=queue`) and must still
 * light up on its page whatever view is showing.
 */
export function isChildActive(children: NavItem[], pathname: string): boolean {
  return children.some((child) => isItemActive(child, pathname));
}

/** Is the page one of the paths this link stands for (sub-routes included)? */
function isItemActive(item: NavItem, pathname: string): boolean {
  return navItemPaths(item).some((href) => {
    const path = href.split(/[?#]/)[0];
    return pathname === path || pathname.startsWith(`${path}/`);
  });
}

/**
 * Should this link match its sub-routes, so it stays lit on a detail page?
 *
 * Yes by default — a picker on /picking/123 must still see Pick Orders lit.
 * The exception is a link whose path is a PREFIX of another nav entry's:
 * On Hand (/inventory) must not also light up on /inventory/bulk-receive,
 * which is its own entry. `/` prefixes everything, so it matches exactly.
 */
export function matchesExactly(href: string, allHrefs: string[]): boolean {
  return href === '/' || allHrefs.some((other) => other.startsWith(`${href}/`));
}

/**
 * A named group (Settings, Reference Data) is a destination and reads like the
 * real nav. A "More" group is an overflow affordance, so it steps back: smaller,
 * muted, lighter — present when wanted, quiet when not. Shared by the flyout and
 * the inline (drawer) rendering so the two can't drift apart.
 */
function groupTriggerClass(
  group: NavGroup,
  active: boolean,
  collapsed: boolean,
) {
  const isMore = group.variant === 'more';

  return cn(
    'flex w-full items-center rounded-md transition-colors',
    isMore
      ? 'gap-2 px-3 py-1 text-xs font-normal'
      : 'gap-3 px-3 py-2 text-sm font-medium',
    // Muted, but a step darker than the /60 section headers: this one is an
    // interactive control, not a caption, and it sits at 12px.
    isMore
      ? 'text-sidebar-foreground/75 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
      : 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
    active &&
      (isMore
        ? 'text-sidebar-foreground'
        : 'bg-sidebar-accent text-sidebar-accent-foreground'),
    !active && !isMore && 'text-sidebar-foreground',
    collapsed && 'justify-center px-2',
  );
}

function collectHrefs(sections: NavSection[]): string[] {
  return sections.flatMap((section) =>
    section.entries.flatMap((entry) =>
      isNavGroup(entry)
        ? entry.children.flatMap((c) => navItemPaths(c))
        : navItemPaths(entry),
    ),
  );
}

interface SidebarLinkProps {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}

/** A top-level entry that is a plain route link. */
function SidebarLink({ item, collapsed, onNavigate }: SidebarLinkProps) {
  const Icon = item.icon;
  // NavLink only knows `href`; a link with alternates is also lit on those.
  const { pathname } = useLocation();
  const onAlternate = !!item.alternates && isItemActive(item, pathname);

  const navLink = (
    <NavLink
      to={item.href}
      end={matchesExactly(item.href, NAV_HREFS)}
      onClick={onNavigate}
      // Collapsed, the link is icon-only: the tooltip is a visual label, not
      // an accessible name.
      aria-label={collapsed ? item.name : undefined}
      data-testid={`nav-${testId(item.name)}`}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          isActive || onAlternate
            ? 'bg-sidebar-primary text-sidebar-primary-foreground'
            : 'text-sidebar-foreground',
          collapsed && 'justify-center px-2',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            size={20}
            weight={isActive || onAlternate ? 'fill' : 'regular'}
            className="shrink-0"
          />
          {!collapsed && <span>{item.name}</span>}
        </>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <li>
        <Tooltip>
          <TooltipTrigger asChild>{navLink}</TooltipTrigger>
          <TooltipContent side="right">{item.name}</TooltipContent>
        </Tooltip>
      </li>
    );
  }

  return <li>{navLink}</li>;
}

/** A child entry inside a flyout panel. */
function FlyoutLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate: () => void;
}) {
  const Icon = item.icon;
  const { pathname } = useLocation();
  const onAlternate = !!item.alternates && isItemActive(item, pathname);

  return (
    <li>
      <NavLink
        to={item.href}
        end={matchesExactly(item.href, NAV_HREFS)}
        onClick={onNavigate}
        data-testid={`nav-${testId(item.name)}`}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            'hover:bg-accent hover:text-accent-foreground',
            isActive || onAlternate
              ? 'bg-accent text-accent-foreground'
              : 'text-foreground',
          )
        }
      >
        {({ isActive }) => (
          <>
            <Icon
              size={18}
              weight={isActive || onAlternate ? 'fill' : 'regular'}
              className="shrink-0"
            />
            <span className="whitespace-nowrap">{item.name}</span>
          </>
        )}
      </NavLink>
    </li>
  );
}

/**
 * Only one flyout may be open at a time, so the open group lives here rather
 * than in each group. Opening is immediate; closing is deferred so the pointer
 * can cross the gap between the trigger and the panel — but hovering another
 * group takes over at once instead of briefly stacking two panels.
 */
function useFlyoutController() {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A hover-opened panel must not pull focus off the page; a keyboard-opened
  // one must, so that arrow keys land inside the submenu.
  const openedByHover = useRef(false);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const open = (key: string, byHover: boolean) => {
    cancelClose();
    openedByHover.current = byHover;
    setOpenGroup(key);
  };

  const close = () => {
    cancelClose();
    setOpenGroup(null);
  };

  const scheduleClose = (key: string) => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setOpenGroup((current) => (current === key ? null : current));
    }, 150);
  };

  // The panel is portaled, so it outlives its trigger being hidden: shrinking
  // the window past the `lg` breakpoint hides the whole sidebar but would
  // strand an open panel on top of the page, swallowing clicks.
  useEffect(() => {
    if (!openGroup) return;
    const onResize = () => setOpenGroup(null);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [openGroup]);

  useEffect(() => () => cancelClose(), []);

  return { openGroup, open, close, scheduleClose, cancelClose, openedByHover };
}

type FlyoutController = ReturnType<typeof useFlyoutController>;

interface SidebarGroupProps {
  group: NavGroup;
  collapsed: boolean;
  onNavigate?: () => void;
}

/** Desktop group: hovering — or clicking, for keyboard — opens a flyout. */
function SidebarFlyoutGroup({
  group,
  collapsed,
  onNavigate,
  flyout,
}: SidebarGroupProps & { flyout: FlyoutController }) {
  const { pathname } = useLocation();

  const Icon = group.icon;
  const active = isChildActive(group.children, pathname);
  const twoColumn = group.children.length > TWO_COLUMN_THRESHOLD;
  const open = flyout.openGroup === group.key;
  const label = group.panelLabel ?? group.name;
  const isMore = group.variant === 'more';

  const cancelClose = flyout.cancelClose;
  const scheduleClose = () => flyout.scheduleClose(group.key);

  return (
    <li
      onMouseEnter={() => flyout.open(group.key, true)}
      onMouseLeave={scheduleClose}
    >
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (next) flyout.open(group.key, false);
          else flyout.close();
        }}
      >
        <PopoverTrigger asChild>
          <button
            data-testid={`nav-group-${group.key}`}
            // Several sections show a group literally called "More", so the
            // accessible name has to come from the fuller label.
            aria-label={label}
            className={groupTriggerClass(group, active, collapsed)}
          >
            <Icon
              size={isMore ? 16 : 20}
              weight={active && !isMore ? 'fill' : 'regular'}
              className="shrink-0"
            />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{group.name}</span>
                <CaretRight
                  size={isMore ? 10 : 12}
                  weight="bold"
                  className="shrink-0"
                />
              </>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => {
            if (flyout.openedByHover.current) e.preventDefault();
          }}
          // Hover, Escape and outside-clicks govern closing. Focus does not:
          // a hover-opened panel is never focused, and closing the previous
          // panel restores focus to its own trigger — which this panel would
          // otherwise read as "focus left me" and immediately dismiss itself.
          // Trade-off: a keyboard user who tabs past the last child leaves the
          // panel open until Escape / a click / hovering away.
          onFocusOutside={(e) => e.preventDefault()}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          className="max-h-[80vh] w-auto overflow-y-auto p-1"
        >
          <div className="text-muted-foreground px-3 py-1.5 text-xs font-medium uppercase tracking-wider">
            {label}
          </div>
          <ul
            className={cn(
              'space-y-0.5',
              twoColumn && 'grid grid-cols-2 gap-x-1',
            )}
          >
            {group.children.map((child) => (
              <FlyoutLink
                key={child.href}
                item={child}
                onNavigate={() => {
                  flyout.close();
                  onNavigate?.();
                }}
              />
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </li>
  );
}

/** Mobile-drawer group: tapping expands the children inline. */
function SidebarInlineGroup({ group, onNavigate }: SidebarGroupProps) {
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState(() =>
    isChildActive(group.children, pathname),
  );

  const Icon = group.icon;
  const isMore = group.variant === 'more';

  return (
    <li>
      <button
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-label={group.panelLabel ?? group.name}
        data-testid={`nav-group-${group.key}`}
        className={groupTriggerClass(group, false, false)}
      >
        <Icon size={isMore ? 16 : 20} weight="regular" className="shrink-0" />
        <span className="flex-1 text-left">{group.name}</span>
        {expanded ? (
          <CaretUp size={isMore ? 10 : 12} weight="bold" className="shrink-0" />
        ) : (
          <CaretDown
            size={isMore ? 10 : 12}
            weight="bold"
            className="shrink-0"
          />
        )}
      </button>
      {expanded && (
        <ul className="mt-1 space-y-1 pl-8">
          {group.children.map((child) => (
            <SidebarLink
              key={child.href}
              item={child}
              collapsed={false}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

const ALL_NAVIGATION: NavSection[] = [
  {
    label: 'Overview',
    hideLabel: true,
    entries: [
      { name: 'Dashboard', href: '/', icon: SquaresFour },
      // Worker-facing: each person's own numbers. useFilteredNavigation hides
      // it for anyone without a measurable role (see /my-stats in
      // lib/permissions.ts).
      { name: 'My Stats', href: '/my-stats', icon: ChartLineUp },
    ],
  },
  {
    label: 'Inbound',
    entries: [{ name: 'Receiving', href: '/purchase-orders', icon: FileText }],
  },
  {
    label: 'Outbound',
    entries: [
      // The shipping team's standing overview. Sits above the SO list because
      // it is the screen they open first each morning; the list is the
      // drill-down from its cards.
      {
        name: 'Live Dashboard',
        href: '/orders-dashboard',
        icon: CalendarBlank,
      },
      { name: 'Sales Orders', href: '/sales-orders', icon: Receipt },
      // Pick Orders / Recount / Wrapping stay one click away rather than
      // behind the flyout: for a picker, recounter or wrapper this is usually
      // the ONLY screen they work in all day.
      { name: 'Pick Orders', href: '/picking', icon: HandGrabbing },
      // Phase-2 gathering. Visibility is automatic via useFilteredNavigation's
      // canAccess(href). NOT added to mobile-nav: that bar is already at its
      // six-item budget and is not permission-filtered.
      { name: 'Gather', href: '/gather', icon: Basket },
      { name: 'Recount', href: '/recount', icon: Checks },
      { name: 'Wrapping', href: '/wrapping', icon: Package },
      {
        name: 'More',
        panelLabel: 'More outbound',
        key: 'outbound-more',
        icon: DotsThreeOutline,
        variant: 'more',
        children: [
          { name: 'Short Picks', href: '/short-picks', icon: Warning },
        ],
      },
    ],
  },
  {
    label: 'Inventory',
    entries: [
      { name: 'On Hand', href: '/inventory', icon: Stack },
      { name: 'Items', href: '/items', icon: Package },
      { name: 'SKU Needs', href: '/sku-needs', icon: ClipboardText },
      // Counting sits standalone (like Recount in Outbound) rather than behind
      // the flyout: for a counter this is the ONLY screen he works in all day.
      { name: 'Counting', href: '/counting', icon: ListNumbers },
      // Standalone like Counting, and for the same reason: an operator with a
      // scanner in hand should not have to open a flyout to resolve a label.
      // Scan sits FIRST of the two — it answers "what is this?" for every kind
      // of code, and Label Lookup is the narrower tool (serials, plus reprint).
      { name: 'Scan', href: '/scan', icon: Scan },
      { name: 'Label Lookup', href: '/label-lookup', icon: Barcode },
      {
        name: 'More',
        panelLabel: 'More inventory',
        key: 'inventory-more',
        icon: DotsThreeOutline,
        variant: 'more',
        children: [
          // COUNT_MANAGER-only (ROUTE_PERMISSIONS) — the manager surface of
          // cycle counting v2, beside the counter's standalone "Counting".
          {
            name: 'Count Batches',
            href: '/count-batches',
            icon: ListNumbers,
          },
          { name: 'Adjustments', href: '/adjustments', icon: Scales },
          // INVENTORY_MANAGER-only. useFilteredNavigation hides this for users
          // without the role at the active warehouse.
          {
            name: 'Bulk Receive',
            href: '/inventory/bulk-receive',
            icon: UploadSimple,
          },
          // INVENTORY_MANAGER-only. useFilteredNavigation hides this for users
          // without the role at the active warehouse.
          {
            name: 'Bulk Move',
            href: '/inventory/bulk-move',
            icon: ArrowsClockwise,
          },
          // INVENTORY_MANAGER-only. Unified history view (ADJUST + RECEIVE +
          // ROLLBACK) on the shared BulkInventoryAdjustmentJob table.
          {
            name: 'Bulk Imports',
            href: '/inventory/bulk-imports',
            icon: ClockCounterClockwise,
          },
          {
            name: 'Transfer Orders',
            href: '/transfer-orders',
            icon: ArrowsLeftRight,
          },
          { name: 'Locations', href: '/locations', icon: MapPin },
        ],
      },
    ],
  },
  {
    label: 'Manufacturing',
    entries: [
      { name: 'Kit Assembly', href: '/kit-assembly', icon: PuzzlePiece },
      { name: 'Kit Putaway', href: '/kit-putaway', icon: Tray },
      // Direct link: the mfg crew's daily page (MANUFACTURER /
      // MANUFACTURING_MANAGER / WAREHOUSE_MANAGER via ROUTE_PERMISSIONS).
      {
        name: 'Mfg Dashboard',
        href: '/manufacturing-dashboard',
        icon: ChartBar,
      },
      // Big-screen board for the plant (2026-10-20): read-only.
      { name: 'Mfg Board', href: '/manufacturing-board', icon: Monitor },
      // Stack-label scan station at the saws (mfg-program PR-H).
      {
        name: 'Manufacturing Scan',
        href: '/manufacturing-scan',
        icon: Scissors,
      },
      // End-of-line count (2026-10-30): the worker at the end of the line
      // counts finished pieces by colour / part / size — closet and kitchen
      // parts on one screen (2026-11-04). Shown while either line's
      // end-count switch is on.
      {
        name: 'Count Parts',
        href: '/manufacturing-count',
        icon: ListChecks,
      },
      {
        name: 'More',
        panelLabel: 'More manufacturing',
        key: 'manufacturing-more',
        icon: DotsThreeOutline,
        variant: 'more',
        children: [
          {
            name: 'Mfg Work Orders',
            // mfg-v2 V2-D (A-Q11): the sidebar opens the operator Queue.
            href: '/manufacturing-work-orders?view=queue',
            icon: Factory,
          },
          {
            name: 'Mfg Settings',
            href: '/settings/manufacturing',
            icon: GearSix,
          },
        ],
      },
    ],
  },
  {
    // Kitchen module (plan kitchen-module-2026-10 §11) — its own area, apart
    // from the closet manufacturing flow. Every entry is hidden by
    // canAccess(href) for roles without the route.
    label: 'Kitchens',
    entries: [
      { name: 'Projects', href: '/kitchens/projects', icon: CookingPot },
      // Kitchen product catalog (K-3): the four kitchen roles read it.
      { name: 'Catalog', href: '/kitchens/catalog', icon: BookOpen },
      // Kitchen planning dashboard (K-8): the four kitchen roles read it.
      { name: 'Planning', href: '/kitchens/planning', icon: ChartLineUp },
      // Kitchens → Engineering (QA H-24): engineering review and Mike
      // Zabloski's engineering names + spec status on one page (the old
      // /kitchens/engineering-names link redirects there); every kitchen
      // role reads, edits are gated per action.
      {
        name: 'Engineering',
        href: '/kitchens/engineering',
        icon: SealCheck,
      },
      // Kitchens → Production (KM-5 / KM-6, Decision 6): kitchen orders on
      // the manufacturing engine — dashboard, work orders, board and scan
      // all under /kitchens/production*, never under Manufacturing.
      { name: 'Production', href: '/kitchens/production', icon: Factory },
      // Kitchens → Acumatica (PR-A2): items per project + failures (every
      // kitchen role); the colour materials page is purchasing's.
      { name: 'Acumatica', href: '/kitchens/acumatica', icon: CloudArrowUp },
      {
        name: 'Acumatica Colours',
        href: '/kitchens/acumatica/colours',
        icon: Palette,
      },
      // Kitchens → Assembly (kitchens end to end PR-L1): finish a cabinet →
      // label 1 with its serial → scan it → assembled.
      { name: 'Assembly', href: '/kitchens/assembly', icon: Barcode },
      // Kitchens → Shipping (kitchens end to end PR-S1): label 2, start a
      // truck, scan each cabinet onto it, ship / finish short.
      { name: 'Shipping', href: '/kitchens/shipping', icon: Truck },
      // Legacy Labels: the old multi-family cabinet-list upload + 6x4 labels
      // (MULTI_FAMILY + WAREHOUSE_MANAGER via ROUTE_PERMISSIONS). Interim
      // (K-18) until the floor switches to Kitchens → Projects.
      { name: 'Legacy Labels', href: '/kitchen-labels', icon: Tag },
    ],
  },
  {
    label: 'Admin',
    entries: [
      // The two settings destinations sit at the top. Settings (the
      // Configuration page) holds printing + printers, picking, gathering,
      // kit assembly, allocation and receiving; Rules is one link for all the
      // rule pages (lib/rule-pages.ts) — it opens the first one the role can see, whose side menu
      // (RulesLayoutPage) lists the rest.
      { name: 'Settings', href: '/settings/configuration', icon: GearSix },
      {
        name: 'Rules',
        href: RULE_PAGES[0].href,
        alternates: RULE_PAGES.slice(1).map((page) => page.href),
        icon: ListChecks,
      },
      {
        name: 'Reference Data',
        key: 'reference-data',
        icon: Database,
        children: [
          { name: 'Warehouses', href: '/warehouses', icon: Warehouse },
          { name: 'UOM', href: '/uom', icon: Ruler },
          { name: 'Vendors', href: '/vendors', icon: Truck },
          // Lists other things point at — not rules (moved out of Rules).
          { name: 'Tags', href: '/settings/tags', icon: TagSimple },
          {
            name: 'Item Type Overrides',
            href: '/settings/item-type-overrides',
            icon: ArrowsLeftRight,
          },
        ],
      },
      {
        name: 'Administration',
        key: 'administration',
        icon: Wrench,
        children: [
          { name: 'Chat', href: '/chat', icon: ChatCircle },
          // Admin-gated: hidden below unless GET /ai-assistant/access allows.
          { name: 'AI Analyst', href: '/assistant', icon: Sparkle },
          {
            name: 'Sync Dashboard',
            href: '/sync-dashboard',
            icon: ArrowsClockwise,
          },
          { name: 'Users', href: '/users', icon: Users },
          { name: 'Reports', href: '/reports', icon: ChartBar },
          { name: 'Actions', href: '/actions', icon: ClockCounterClockwise },
          { name: 'Pallet Photos', href: '/wrap-photos', icon: Images },
          // TEMPORARY FEATURE — external-fulfillment reconcile (admin-only).
          // Remove this nav entry once external fulfillment stops.
          {
            name: 'External Fulfillment Reconcile',
            href: '/external-fulfillment-reconcile',
            icon: Broom,
          },
          { name: 'Notifications', href: '/notifications', icon: Bell },
          { name: 'Workforce', href: '/workforce', icon: UsersThree },
          {
            name: 'Dashboard Columns',
            href: '/settings/dashboard-columns',
            icon: Columns,
          },
        ],
      },
      // Mostly touched by developers; access is unchanged — this only groups.
      {
        name: 'Developer',
        key: 'developer',
        icon: Code,
        children: [
          { name: 'Roles', href: '/settings/roles', icon: ShieldCheck },
          {
            name: 'IP Allowlist',
            href: '/settings/ip-allowlist',
            icon: Shield,
          },
          { name: 'API Keys', href: '/settings/api-keys', icon: Key },
          {
            name: 'Label Templates',
            href: '/settings/label-templates',
            icon: Tag,
          },
        ],
      },
    ],
  },
];

/** Every routable href in the nav — used to decide which links match sub-routes. */
export const NAV_HREFS = collectHrefs(ALL_NAVIGATION);

/** Drops one href from the nav tree, pruning groups/sections left empty. */
function removeNavHref(sections: NavSection[], href: string): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      entries: section.entries
        .map((entry) =>
          isNavGroup(entry)
            ? {
                ...entry,
                children: entry.children.filter((c) => c.href !== href),
              }
            : entry,
        )
        .filter((entry) =>
          isNavGroup(entry) ? entry.children.length > 0 : entry.href !== href,
        ),
    }))
    .filter((section) => section.entries.length > 0);
}

export function Sidebar({
  collapsed,
  onToggle,
  onNavigate,
  inlineSubmenus = false,
}: SidebarProps) {
  const roleFilteredNavigation = useFilteredNavigation(ALL_NAVIGATION);
  // The AI Analyst entry is additionally owner-gated (email allowlist on the
  // server) — the role registry alone would show it to every authenticated user.
  const { data: aiAccess } = useAiAssistantAccess();
  // The stack-label scan station only while saws print labels (saw labels
  // setting; off since 2026-10-20 — saw lines are marked completed instead).
  const sawLabelsOn = useSawLabelsEnabled();
  // Count Parts shows closet and kitchen parts (2026-11-04): either switch.
  const closetCountOn = useEndCountEnabled();
  const kitchenCountOn = useKitchenEndCountOn();
  const endCountOn = closetCountOn || kitchenCountOn;
  // Short-pick fill flow (plan §4 S4, interim): once the selected warehouse is
  // switched on, short picks are worked from Sales Orders → Short and the
  // recount queue, so the old page leaves the nav (it keeps a "moved" banner
  // until S6 retires it). Admin "All" mode has no warehouse → entry stays.
  const { warehouseId: activeWarehouseId } = useActiveWarehouse();
  const { data: fillFlow } = useFillFlow(activeWarehouseId);
  const filteredNavigation = useMemo(() => {
    let nav = aiAccess?.allowed
      ? roleFilteredNavigation
      : removeNavHref(roleFilteredNavigation, '/assistant');
    if (!sawLabelsOn) nav = removeNavHref(nav, '/manufacturing-scan');
    if (!endCountOn) nav = removeNavHref(nav, '/manufacturing-count');
    if (fillFlow?.enabled) nav = removeNavHref(nav, '/short-picks');
    return nav;
  }, [
    roleFilteredNavigation,
    aiAccess?.allowed,
    sawLabelsOn,
    endCountOn,
    fillFlow?.enabled,
  ]);
  const flyout = useFlyoutController();

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        'bg-sidebar text-sidebar-foreground border-sidebar-border flex h-screen flex-col border-r transition-all duration-300',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Logo */}
      <div
        className={cn(
          'border-sidebar-border flex h-14 items-center border-b',
          collapsed ? 'justify-center px-2' : 'gap-2 px-4',
        )}
      >
        <img
          src="/logo-mark.svg"
          alt="WMS Pro logo"
          className="h-9 w-9 shrink-0"
        />
        {!collapsed && (
          <span className="text-lg font-semibold tracking-tight">WMS Pro</span>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1">
        <nav className="p-2">
          <TooltipProvider delayDuration={0}>
            <ul className="space-y-1">
              {filteredNavigation.map((section) => (
                <li key={section.label}>
                  {!section.hideLabel && (
                    <div className="py-2">
                      {collapsed ? (
                        <div className="border-sidebar-border mx-2 border-t" />
                      ) : (
                        <span className="text-sidebar-foreground/60 px-3 text-xs font-medium uppercase tracking-wider">
                          {section.label}
                        </span>
                      )}
                    </div>
                  )}
                  <ul className="space-y-1">
                    {section.entries.map((entry) => {
                      if (!isNavGroup(entry)) {
                        return (
                          <SidebarLink
                            key={entry.href}
                            item={entry}
                            collapsed={collapsed}
                            onNavigate={onNavigate}
                          />
                        );
                      }

                      return inlineSubmenus ? (
                        <SidebarInlineGroup
                          key={entry.key}
                          group={entry}
                          collapsed={collapsed}
                          onNavigate={onNavigate}
                        />
                      ) : (
                        <SidebarFlyoutGroup
                          key={entry.key}
                          group={entry}
                          collapsed={collapsed}
                          onNavigate={onNavigate}
                          flyout={flyout}
                        />
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          </TooltipProvider>
        </nav>
      </ScrollArea>

      {/* Documentation is available to every signed-in user. */}
      <nav aria-label="Help" className="border-sidebar-border border-t p-2">
        <ul>
          <SidebarLink
            item={{
              name: 'Knowledge base',
              href: '/knowledge-base',
              icon: BookOpen,
            }}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        </ul>
      </nav>

      {/* Collapse toggle */}
      <div className="border-sidebar-border border-t p-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          // 44 px: on the floor tablets this is the only way back to the menu.
          className="h-11 w-full justify-center"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <CaretRight size={16} weight="bold" />
          ) : (
            <>
              <CaretLeft size={16} weight="bold" />
              <span className="ml-2">Collapse</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  );
}
